import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PortalLayout from "@/components/PortalLayout";
import { api, apiErr, BACKEND_URL } from "@/lib/api";
import { usePortalAuth } from "@/context/PortalAuth";
import RichTextEditor from "@/components/RichTextEditor";
import { sanitizeHtml } from "@/lib/sanitize";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Send, FileText, ScrollText, ClipboardList, MessageSquare, UserCog, ShieldCheck, Reply as ReplyIcon, CalendarDays, Plus, X, Trash2 } from "lucide-react";

const ROLE_LABEL = { Chairman: "Chairman", Treasurer: "Treasurer", Secretary: "Secretary", SocietyManager: "Society Manager" };

export default function CommitteeDashboard() {
  const { slug } = useParams();
  const { me } = usePortalAuth();
  const [ctx, setCtx] = useState(null);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("overview");

  const tid = me?.society?.id;

  useEffect(() => {
    (async () => {
      try {
        const c = await api.get("/portal/committee/context");
        setCtx(c.data);
      } catch (e) { /* not committee */ }
    })();
  }, []);

  useEffect(() => {
    if (!tid) return;
    api.get(`/tenants/${tid}/committee/stats`).then(({ data }) => setStats(data)).catch(() => {});
  }, [tid, tab]);

  const perms = new Set(ctx?.permissions || []);

  if (ctx === null) {
    return <PortalLayout title="Committee"><div className="text-stone-500 text-sm">Loading…</div></PortalLayout>;
  }
  if (!ctx?.is_committee) {
    return (
      <PortalLayout subtitle="Committee" title="Not a committee member">
        <div className="bg-white border border-stone-200 rounded-2xl p-8 text-stone-600">
          You do not currently hold a committee role in this society. If this is an error, please contact your Society Manager or the portal administrator.
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout
      subtitle="Committee workspace"
      title="Committee Dashboard"
      actions={
        <div className="hidden md:flex items-center gap-2">
          {ctx.committee_roles.map((r) => (
            <span key={r} className="inline-flex items-center gap-1 rounded-full bg-terracotta/10 border border-terracotta/30 text-terracotta px-3 py-1 text-xs font-medium">
              <ShieldCheck size={12}/> {ROLE_LABEL[r] || r}
            </span>
          ))}
        </div>
      }
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <Stat icon={FileText} label="Pending ownership" value={stats?.pending_ownership ?? "—"}/>
        <Stat icon={ClipboardList} label="Pending applications" value={stats?.pending_applications ?? "—"}/>
        <Stat icon={MessageSquare} label="Open complaints" value={stats?.open_complaints ?? "—"}/>
        <Stat icon={FileText} label="Draft invoices" value={stats?.draft_invoices ?? "—"}/>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-stone-200">
        <TabBtn on={tab === "overview"} onClick={() => setTab("overview")} testid="ctab-overview" icon={UserCog}>Overview</TabBtn>
        <TabBtn on={tab === "notices"} onClick={() => setTab("notices")} testid="ctab-notices" icon={ScrollText} disabled={!perms.has("post_notice")}>Draft notice</TabBtn>
        <TabBtn on={tab === "complaints"} onClick={() => setTab("complaints")} testid="ctab-complaints" icon={MessageSquare} disabled={!perms.has("resolve_complaint")}>Complaints</TabBtn>
        <TabBtn on={tab === "applications"} onClick={() => setTab("applications")} testid="ctab-applications" icon={ClipboardList} disabled={!perms.has("approve_application")}>Applications</TabBtn>
        <TabBtn on={tab === "invoices"} onClick={() => setTab("invoices")} testid="ctab-invoices" icon={FileText} disabled={!perms.has("post_invoice")}>Invoices</TabBtn>
        <TabBtn on={tab === "ownership"} onClick={() => setTab("ownership")} testid="ctab-ownership" icon={UserCog} disabled={!perms.has("approve_ownership") && !perms.has("create_ownership")}>Ownership</TabBtn>
        <TabBtn on={tab === "responses"} onClick={() => setTab("responses")} testid="ctab-responses" icon={ReplyIcon} disabled={!perms.has("reply_thread")}>Responses</TabBtn>
        <TabBtn on={tab === "facilities"} onClick={() => setTab("facilities")} testid="ctab-facilities" icon={CalendarDays} disabled={!perms.has("manage_resources") && !perms.has("approve_booking")}>Facilities</TabBtn>
      </div>

      {tab === "overview" && <Overview stats={stats} perms={perms} setTab={setTab}/>}
      {tab === "notices" && perms.has("post_notice") && <NoticeComposer tid={tid}/>}
      {tab === "complaints" && perms.has("resolve_complaint") && <ComplaintsQueue tid={tid}/>}
      {tab === "applications" && perms.has("approve_application") && <ApplicationsQueue tid={tid}/>}
      {tab === "invoices" && perms.has("post_invoice") && <InvoicesQueue tid={tid}/>}
      {tab === "ownership" && (perms.has("approve_ownership") || perms.has("create_ownership")) && <OwnershipQueue tid={tid} perms={perms}/>}
      {tab === "responses" && perms.has("reply_thread") && <ResponsesQueue tid={tid} perms={perms}/>}
      {tab === "facilities" && (perms.has("manage_resources") || perms.has("approve_booking")) && <FacilitiesAdmin tid={tid} perms={perms}/>}
    </PortalLayout>
  );
}

function TabBtn({ on, onClick, icon: Icon, children, disabled, testid }) {
  return (
    <button onClick={disabled ? undefined : onClick} disabled={disabled} data-testid={testid}
      className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-2 transition-colors ${on ? "border-terracotta text-ink" : "border-transparent text-stone-500 hover:text-ink"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
      <Icon size={14}/> {children}
    </button>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4">
      <div className="flex items-center justify-between text-stone-500 text-[11px] uppercase tracking-widest font-semibold">
        {label}<Icon size={14} strokeWidth={1.5}/>
      </div>
      <div className="editorial text-3xl mt-2">{value}</div>
    </div>
  );
}

function Overview({ stats, perms, setTab }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="bg-white border border-stone-200 rounded-2xl p-6">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-2">Society at a glance</div>
        <div className="text-sm text-stone-600 space-y-1.5">
          <div>Total units: <b>{stats?.total_units ?? "—"}</b></div>
          <div>Occupied by tenants: <b>{stats?.occupied_by_tenant ?? "—"}</b></div>
          <div>Outstanding posted invoices: <b>{stats?.outstanding_invoices ?? "—"}</b></div>
        </div>
      </div>
      <div className="bg-white border border-stone-200 rounded-2xl p-6">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-2">Your permissions</div>
        <ul className="text-sm text-stone-700 space-y-1">
          {Array.from(perms).map((p) => <li key={p} className="inline-flex items-center gap-2"><CheckCircle2 size={13} className="text-emerald-700"/> {p.replace(/_/g, " ")}</li>)}
        </ul>
      </div>
    </div>
  );
}

function NoticeComposer({ tid }) {
  const [form, setForm] = useState({ title: "", body: "", category: "General", target: "All" });
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);

  const load = () => api.get(`/tenants/${tid}/notices`).then(({ data }) => setRecent(data.slice(0, 5)));
  useEffect(() => { if (tid) load(); /* eslint-disable-next-line */ }, [tid]);

  const submit = async () => {
    if (!form.title || !form.body) { toast.error("Add a title and some body content"); return; }
    setBusy(true);
    try {
      await api.post(`/tenants/${tid}/notices`, form);
      toast.success("Notice issued and PDF generated");
      setForm({ title: "", body: "", category: "General", target: "All" });
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-white border border-stone-200 rounded-2xl p-6 space-y-4">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Draft a notice</div>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Notice title"
          data-testid="ctee-notice-title"
          className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-lg outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20"/>
        <div className="grid grid-cols-2 gap-3">
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl border border-stone-300 bg-white px-4 py-3 outline-none focus:border-terracotta">
            {["General", "Meeting", "Minutes", "Tenant", "Broadcast", "Other"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <select value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className="rounded-xl border border-stone-300 bg-white px-4 py-3 outline-none focus:border-terracotta">
            {["All", "Owners", "Tenants"].map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <RichTextEditor value={form.body} onChange={(html) => setForm({ ...form, body: html })} placeholder="Draft the notice — use headings, lists, bold, links…" testid="ctee-notice-body"/>
        <div className="flex justify-end">
          <button onClick={submit} disabled={busy} data-testid="ctee-notice-submit"
            className="rounded-full bg-terracotta hover:bg-terracotta-hover disabled:opacity-50 text-white px-6 py-3 text-sm font-medium">
            {busy ? "Publishing…" : "Publish notice"}
          </button>
        </div>
      </div>
      <div className="space-y-3">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Recently issued</div>
        {recent.map((n) => (
          <div key={n.id} className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="text-xs uppercase tracking-widest text-terracotta font-semibold">{n.category}</div>
            <div className="editorial text-base mt-0.5">{n.title}</div>
            <a href={`${BACKEND_URL}/api/tenants/${tid}/notices/${n.id}/pdf`} target="_blank" rel="noreferrer"
              className="text-terracotta text-xs mt-2 inline-block">PDF ↗</a>
          </div>
        ))}
        {recent.length === 0 && <div className="text-sm text-stone-500">No notices yet.</div>}
      </div>
    </div>
  );
}

function ComplaintsQueue({ tid }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get(`/tenants/${tid}/complaints`).then(({ data }) => setRows(data));
  useEffect(() => { if (tid) load(); /* eslint-disable-next-line */ }, [tid]);
  const setStatus = async (id, status) => {
    try { await api.post(`/tenants/${tid}/complaints/${id}/status`, { status, remarks: "" }); toast.success("Updated"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
      <table className="w-full text-left text-sm">
        <thead className="bg-sand-50 border-b border-stone-200"><tr>{["Subject", "Raised by", "Status", ""].map((h, i) => <th key={i} className="text-xs uppercase tracking-wider font-semibold text-stone-500 py-3 px-4">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className="border-t border-stone-100" data-testid={`c-row-${c.id}`}>
              <td className="py-3 px-4"><div className="font-medium">{c.subject}</div><div className="text-xs text-stone-500 line-clamp-1">{c.description}</div></td>
              <td className="py-3 px-4">{c.raised_by_name}<div className="text-xs text-stone-500">{c.raised_by_role}</div></td>
              <td className="py-3 px-4">{c.status}</td>
              <td className="py-3 px-4 text-right">
                <div className="flex gap-2 justify-end">
                  {c.status !== "In Progress" && <button onClick={() => setStatus(c.id, "In Progress")} className="text-amber-700 text-xs font-medium">In Progress</button>}
                  {c.status !== "Resolved" && <button onClick={() => setStatus(c.id, "Resolved")} className="text-emerald-700 text-xs font-medium">Resolve</button>}
                </div>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan="4" className="p-10 text-center text-stone-500">No complaints raised.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ApplicationsQueue({ tid }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get(`/tenants/${tid}/applications`).then(({ data }) => setRows(data));
  useEffect(() => { if (tid) load(); /* eslint-disable-next-line */ }, [tid]);
  const decide = async (id, approve) => {
    try { await api.post(`/tenants/${tid}/applications/${id}/decide`, { approve, remarks: "" }); toast.success(approve ? "Approved" : "Rejected"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
      <table className="w-full text-left text-sm">
        <thead className="bg-sand-50 border-b border-stone-200"><tr>{["Type", "Applicant", "Details", "Status", ""].map((h, i) => <th key={i} className="text-xs uppercase tracking-wider font-semibold text-stone-500 py-3 px-4">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} className="border-t border-stone-100" data-testid={`app-row-${a.id}`}>
              <td className="py-3 px-4">{a.type}</td>
              <td className="py-3 px-4">{a.applicant_name}</td>
              <td className="py-3 px-4 max-w-[300px]"><div className="line-clamp-2 text-stone-600">{a.details}</div></td>
              <td className="py-3 px-4">{a.status}</td>
              <td className="py-3 px-4 text-right">
                {a.status === "Submitted" && <div className="flex gap-2 justify-end">
                  <button onClick={() => decide(a.id, true)} className="text-emerald-700 text-xs font-medium inline-flex items-center gap-1"><CheckCircle2 size={12}/>Approve</button>
                  <button onClick={() => decide(a.id, false)} className="text-terracotta text-xs font-medium inline-flex items-center gap-1"><XCircle size={12}/>Reject</button>
                </div>}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan="5" className="p-10 text-center text-stone-500">No applications submitted.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function InvoicesQueue({ tid }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get(`/tenants/${tid}/invoices?status=Draft`).then(({ data }) => setRows(data));
  useEffect(() => { if (tid) load(); /* eslint-disable-next-line */ }, [tid]);
  const post = async (id) => { try { await api.post(`/tenants/${tid}/invoices/${id}/post`); toast.success("Posted"); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
      <table className="w-full text-left text-sm">
        <thead className="bg-sand-50 border-b border-stone-200"><tr>{["Unit", "Period", "Total", "Due", ""].map((h, i) => <th key={i} className="text-xs uppercase tracking-wider font-semibold text-stone-500 py-3 px-4">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-stone-100" data-testid={`inv-row-${r.id}`}>
              <td className="py-3 px-4">{r.unit_label}</td>
              <td className="py-3 px-4">{r.period_label}</td>
              <td className="py-3 px-4">₹ {r.total?.toLocaleString?.() ?? r.total}</td>
              <td className="py-3 px-4">{r.due_date}</td>
              <td className="py-3 px-4 text-right">
                <button onClick={() => post(r.id)} className="text-emerald-700 text-xs font-medium inline-flex items-center gap-1"><Send size={12}/>Post</button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan="5" className="p-10 text-center text-stone-500">No draft invoices to post.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function OwnershipQueue({ tid, perms }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get(`/tenants/${tid}/ownership-changes?status=Pending`).then(({ data }) => setRows(data));
  useEffect(() => { if (tid) load(); /* eslint-disable-next-line */ }, [tid]);
  const decide = async (id, approve) => {
    try { await api.post(`/tenants/${tid}/ownership-changes/${id}/decide`, { approve, remarks: "" }); toast.success(approve ? "Approved" : "Rejected"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
      <table className="w-full text-left text-sm">
        <thead className="bg-sand-50 border-b border-stone-200"><tr>{["Unit", "Proposed owners", "Effective", "Maker", ""].map((h, i) => <th key={i} className="text-xs uppercase tracking-wider font-semibold text-stone-500 py-3 px-4">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-stone-100">
              <td className="py-3 px-4 font-mono text-xs">{r.unit_id?.slice(0, 8)}</td>
              <td className="py-3 px-4">{(r.new_owners || []).map((o) => o.name).join(", ")}</td>
              <td className="py-3 px-4">{r.effective_date}</td>
              <td className="py-3 px-4">{r.maker_name}<div className="text-xs text-stone-500">{r.maker_role}</div></td>
              <td className="py-3 px-4 text-right">
                {perms.has("approve_ownership") ? (
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => decide(r.id, true)} className="text-emerald-700 text-xs font-medium inline-flex items-center gap-1"><CheckCircle2 size={12}/>Approve</button>
                    <button onClick={() => decide(r.id, false)} className="text-terracotta text-xs font-medium inline-flex items-center gap-1"><XCircle size={12}/>Reject</button>
                  </div>
                ) : <span className="text-xs text-stone-500">Awaiting Chairman</span>}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan="5" className="p-10 text-center text-stone-500">No pending ownership changes.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}


// ---------- Responses (Notice-Reply threads) ----------
function ResponsesQueue({ tid, perms }) {
  const [grouped, setGrouped] = useState({ notice: [], invoice: [], application: [] });
  const [openThread, setOpenThread] = useState(null);
  const load = () => api.get(`/tenants/${tid}/replies/pending`).then(({ data }) => setGrouped(data));
  useEffect(() => { if (tid) load(); /* eslint-disable-next-line */ }, [tid]);

  const total = (grouped.notice?.length || 0) + (grouped.invoice?.length || 0) + (grouped.application?.length || 0);

  return (
    <div className="space-y-6">
      <div className="text-sm text-stone-500">{total} thread{total === 1 ? "" : "s"} awaiting committee response.</div>
      {["notice", "invoice", "application"].map((typ) => (
        <div key={typ}>
          <h3 className="editorial text-lg mb-2 capitalize pl-3 border-l-2 border-terracotta">{typ}s ({(grouped[typ] || []).length})</h3>
          <div className="space-y-2">
            {(grouped[typ] || []).map((t) => (
              <button key={t.id} onClick={() => setOpenThread(t)} data-testid={`th-${t.id}`}
                className="w-full text-left bg-white border border-stone-200 hover:border-terracotta rounded-xl p-4 flex items-center gap-4 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{t.entity_title || `${typ} #${t.entity_id?.slice(0, 8)}`}</div>
                  <div className="text-xs text-stone-500 mt-0.5 line-clamp-1">Latest: {t.messages?.[t.messages.length - 1]?.text || "—"}</div>
                </div>
                <div className="text-xs text-stone-500">{t.last_message_at?.slice(0, 16).replace("T", " ")}</div>
              </button>
            ))}
            {(grouped[typ] || []).length === 0 && <div className="text-sm text-stone-400 pl-3">Nothing pending.</div>}
          </div>
        </div>
      ))}
      {openThread && <CommitteeThreadModal tid={tid} thread={openThread} onClose={() => { setOpenThread(null); load(); }} canResolve={perms.has("resolve_thread")}/>}
    </div>
  );
}

function CommitteeThreadModal({ tid, thread, onClose, canResolve }) {
  const [full, setFull] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/tenants/${tid}/replies/${thread.id}`).then(({ data }) => setFull(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [thread.id]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tenants/${tid}/replies/${thread.id}/reply`, { text: text.trim() });
      setText(""); await load();
      toast.success("Reply sent to owner");
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  const resolve = async () => {
    if (!window.confirm("Mark this thread as resolved? The owner won't be able to reply further.")) return;
    try { await api.post(`/tenants/${tid}/replies/${thread.id}/resolve`); toast.success("Thread resolved"); onClose(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl border border-stone-200 flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()} data-testid="ctee-thread-modal">
        <div className="flex items-start justify-between px-6 py-4 border-b border-stone-200">
          <div>
            <div className="text-xs uppercase tracking-widest text-terracotta font-semibold">{thread.entity_type} thread</div>
            <div className="editorial text-lg mt-0.5">{thread.entity_title || thread.entity_id}</div>
          </div>
          <button onClick={onClose} className="p-2 text-stone-500 hover:text-ink"><X size={20}/></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 bg-sand-50 min-h-[240px]">
          {(full?.messages || []).map((m) => (
            <div key={m._id || m.created_at} className={`flex ${m.sender_kind === "committee" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.sender_kind === "committee" ? "bg-terracotta text-white" : "bg-white border border-stone-200"}`}>
                <div className={`text-[11px] uppercase tracking-widest mb-1 ${m.sender_kind === "committee" ? "text-white/70" : "text-stone-500"}`}>{m.sender_name} · {(m.created_at || "").slice(0, 16).replace("T", " ")}</div>
                <div className="whitespace-pre-wrap leading-relaxed">{m.text}</div>
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={send} className="border-t border-stone-200 p-3 flex items-end gap-2">
          <textarea rows="2" value={text} onChange={(e) => setText(e.target.value)} placeholder="Reply to owner…"
            data-testid="ctee-reply-input" disabled={full?.status === "resolved"}
            className="flex-1 rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 resize-none"/>
          <div className="flex flex-col gap-2">
            <button type="submit" disabled={busy || !text.trim() || full?.status === "resolved"} data-testid="ctee-reply-send"
              className="inline-flex items-center gap-1 rounded-full bg-terracotta hover:bg-terracotta-hover disabled:opacity-50 text-white px-4 py-2 text-sm font-medium"><Send size={14}/> Send</button>
            {canResolve && full?.status !== "resolved" && (
              <button type="button" onClick={resolve} data-testid="ctee-reply-resolve"
                className="rounded-full border border-stone-300 hover:bg-sand-100 text-stone-700 px-4 py-2 text-xs">Resolve</button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------- Facilities: Resource Master + Bookings queue + Blackouts ----------
function FacilitiesAdmin({ tid, perms }) {
  const [sub, setSub] = useState("resources");
  return (
    <div>
      <div className="flex gap-2 mb-4">
        <button onClick={() => setSub("resources")} data-testid="fsub-resources" className={`px-4 py-2 rounded-full text-sm ${sub === "resources" ? "bg-ink text-white" : "border border-stone-300 bg-white"}`}>Resources</button>
        <button onClick={() => setSub("bookings")} data-testid="fsub-bookings" className={`px-4 py-2 rounded-full text-sm ${sub === "bookings" ? "bg-ink text-white" : "border border-stone-300 bg-white"}`}>Bookings</button>
      </div>
      {sub === "resources" && <ResourcesAdmin tid={tid} canManage={perms.has("manage_resources")}/>}
      {sub === "bookings" && <BookingsAdmin tid={tid} canApprove={perms.has("approve_booking")}/>}
    </div>
  );
}

function ResourcesAdmin({ tid, canManage }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null); // null | 'new' | resource
  const load = () => api.get(`/tenants/${tid}/facilities/resources`).then(({ data }) => setRows(data));
  useEffect(() => { if (tid) load(); /* eslint-disable-next-line */ }, [tid]);
  return (
    <div>
      {canManage && (
        <div className="mb-4">
          <button onClick={() => setOpen("new")} data-testid="new-resource"
            className="inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-4 py-2 text-sm">
            <Plus size={14}/> New resource
          </button>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {rows.map((r) => (
          <div key={r.id} className="bg-white border border-stone-200 rounded-2xl p-5" data-testid={`res-row-${r.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="editorial text-xl">{r.name}</div>
                <div className="text-xs text-stone-500 mt-0.5">{r.booking_type === "time_slot" ? `${r.slot_duration_hours}h slots` : "Full-day"} · {r.approval_mode === "auto_confirm" ? "Auto" : "Committee approval"} · ₹ {r.fee || 0}</div>
                {r.description && <div className="text-sm text-stone-600 mt-2 line-clamp-2">{r.description}</div>}
              </div>
              {!r.active && <span className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">Inactive</span>}
            </div>
            {canManage && (
              <div className="mt-3 flex gap-2">
                <button onClick={() => setOpen(r)} className="text-xs text-terracotta">Edit</button>
                {r.active && <button onClick={async () => { await api.delete(`/tenants/${tid}/facilities/resources/${r.id}`); toast.success("Deactivated"); load(); }} className="text-xs text-stone-500">Deactivate</button>}
              </div>
            )}
          </div>
        ))}
        {rows.length === 0 && <div className="md:col-span-2 bg-white border border-stone-200 rounded-2xl p-8 text-center text-stone-500">No resources yet.</div>}
      </div>
      {open && <ResourceModal tid={tid} initial={open === "new" ? null : open} onClose={() => { setOpen(null); load(); }}/>}
    </div>
  );
}

function ResourceModal({ tid, initial, onClose }) {
  const [f, setF] = useState(initial || {
    name: "", description: "", capacity: 0, photos: [],
    booking_type: "time_slot", slot_duration_hours: 2, open_time: "06:00", close_time: "22:00",
    advance_days: 30, min_notice_hours: 0, approval_mode: "requires_approval", fee: 0,
    cancellation_policy: "", active: true,
  });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (initial) await api.patch(`/tenants/${tid}/facilities/resources/${initial.id}`, f);
      else await api.post(`/tenants/${tid}/facilities/resources`, f);
      toast.success("Saved"); onClose();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-2xl border border-stone-200 flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()} data-testid="resource-modal">
        <div className="flex items-start justify-between px-6 py-4 border-b border-stone-200">
          <div className="editorial text-xl">{initial ? "Edit resource" : "New resource"}</div>
          <button onClick={onClose} className="p-2 text-stone-500"><X size={18}/></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-3 overflow-y-auto text-sm">
          <IF label="Name" value={f.name} onChange={(v) => setF({ ...f, name: v })} required testid="res-name"/>
          <IF label="Description" value={f.description} onChange={(v) => setF({ ...f, description: v })}/>
          <div className="grid grid-cols-2 gap-3">
            <IF label="Capacity" type="number" value={f.capacity} onChange={(v) => setF({ ...f, capacity: Number(v) })}/>
            <IF label="Fee (₹)" type="number" value={f.fee} onChange={(v) => setF({ ...f, fee: Number(v) })}/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <SF label="Booking type" value={f.booking_type} onChange={(v) => setF({ ...f, booking_type: v })} options={[["time_slot", "Time slots"], ["full_day", "Full day"]]}/>
            <SF label="Approval mode" value={f.approval_mode} onChange={(v) => setF({ ...f, approval_mode: v })} options={[["requires_approval", "Requires approval"], ["auto_confirm", "Auto-confirm"]]}/>
          </div>
          {f.booking_type === "time_slot" && (
            <div className="grid grid-cols-3 gap-3">
              <IF label="Slot hours" type="number" value={f.slot_duration_hours} onChange={(v) => setF({ ...f, slot_duration_hours: Number(v) })}/>
              <IF label="Opens" value={f.open_time} onChange={(v) => setF({ ...f, open_time: v })}/>
              <IF label="Closes" value={f.close_time} onChange={(v) => setF({ ...f, close_time: v })}/>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <IF label="Advance days" type="number" value={f.advance_days} onChange={(v) => setF({ ...f, advance_days: Number(v) })}/>
            <IF label="Min notice (hours)" type="number" value={f.min_notice_hours} onChange={(v) => setF({ ...f, min_notice_hours: Number(v) })}/>
          </div>
          <IF label="Cancellation policy" value={f.cancellation_policy} onChange={(v) => setF({ ...f, cancellation_policy: v })}/>
          <button type="submit" disabled={busy} data-testid="res-save"
            className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-6 py-3 text-sm font-medium disabled:opacity-50">
            {busy ? "Saving…" : "Save resource"}
          </button>
        </form>
      </div>
    </div>
  );
}

function IF({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{label}</span>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid}
        className="mt-1 w-full rounded-xl border border-stone-300 bg-white px-3 py-2 outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20"/>
    </label>
  );
}
function SF({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-stone-300 bg-white px-3 py-2 outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

function BookingsAdmin({ tid, canApprove }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("Pending");
  const load = () => api.get(`/tenants/${tid}/facilities/bookings${filter !== "All" ? `?status=${filter}` : ""}`).then(({ data }) => setRows(data));
  useEffect(() => { if (tid) load(); /* eslint-disable-next-line */ }, [tid, filter]);
  const decide = async (id, approve) => {
    const remarks = approve ? "" : (window.prompt("Reason for rejection (optional):") || "");
    try { await api.post(`/tenants/${tid}/facilities/bookings/${id}/decide`, { approve, remarks }); toast.success(approve ? "Booking confirmed" : "Rejected"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        {["Pending", "Confirmed", "Rejected", "Cancelled", "All"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`fbook-filter-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs ${filter === s ? "bg-ink text-white" : "border border-stone-300 bg-white"}`}>{s}</button>
        ))}
      </div>
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-sand-50 border-b border-stone-200"><tr>{["Resource", "Requested by", "Date", "Slot", "Status", ""].map((h, i) => <th key={i} className="text-xs uppercase tracking-wider font-semibold text-stone-500 py-3 px-4">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id} className="border-t border-stone-100" data-testid={`fbook-${b.id}`}>
                <td className="py-3 px-4">{b.resource_name}</td>
                <td className="py-3 px-4">{b.requested_by_name}<div className="text-xs text-stone-500">{b.purpose}</div></td>
                <td className="py-3 px-4">{b.date}</td>
                <td className="py-3 px-4">{b.slot_start ? `${b.slot_start}–${b.slot_end}` : "Full day"}</td>
                <td className="py-3 px-4">{b.status}</td>
                <td className="py-3 px-4 text-right">
                  {b.status === "Pending" && canApprove && (
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => decide(b.id, true)} className="text-emerald-700 text-xs font-medium inline-flex items-center gap-1"><CheckCircle2 size={12}/>Approve</button>
                      <button onClick={() => decide(b.id, false)} className="text-terracotta text-xs font-medium inline-flex items-center gap-1"><XCircle size={12}/>Reject</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="6" className="p-10 text-center text-stone-500">No bookings.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
